import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import StringField from '../../../../core/components/Fields/StringField.tsx';
import SelectField from '../../../../core/components/Fields/SelectField.tsx';
import Switch from '../../../../core/components/Switch.tsx';
import { useActionUpdate } from '../hooks/useActionUpdate.ts';
import type { AssignLiteralAction } from '../types.ts';

interface AssignLiteralBlockProps {
    action: AssignLiteralAction;
    mappingIndex: number;
    actionIndex: number;
    onUpdate?: (updates: Partial<AssignLiteralAction>) => void;
}

type ValueType = 'string' | 'number' | 'boolean' | 'json';

const AssignLiteralBlock = ({ action, mappingIndex, actionIndex, onUpdate }: AssignLiteralBlockProps) => {
    const { t } = useTranslation('integrations');
    const handleUpdate = useActionUpdate<AssignLiteralAction>(mappingIndex, actionIndex, onUpdate);

    const inferValueType = (): ValueType => {
        if (typeof action.value === 'boolean') return 'boolean';
        if (typeof action.value === 'number') return 'number';
        if (typeof action.value === 'object') return 'json';
        return 'string';
    };

    const [valueType, setValueType] = useState<ValueType>(inferValueType);

    const handleValueChange = (newValue: string | boolean) => {
        let parsedValue: any = newValue;

        if (valueType === 'number') {
            parsedValue = parseFloat(newValue as string) || 0;
        } else if (valueType === 'json') {
            try {
                parsedValue = JSON.parse(newValue as string);
            } catch {
                parsedValue = newValue;
            }
        }

        handleUpdate({ value: parsedValue });
    };

    const handleTypeChange = (newType: string) => {
        setValueType(newType as ValueType);

        let convertedValue: any;
        switch (newType) {
            case 'string':
                convertedValue = String(action.value ?? '');
                break;
            case 'number':
                convertedValue = Number(action.value) || 0;
                break;
            case 'boolean':
                convertedValue = Boolean(action.value);
                break;
            case 'json':
                convertedValue = typeof action.value === 'object' ? action.value : { value: action.value };
                break;
            default:
                convertedValue = action.value;
        }

        handleUpdate({ value: convertedValue });
    };

    const getStringValue = (): string => {
        if (valueType === 'json') {
            return typeof action.value === 'object' ? JSON.stringify(action.value, null, 2) : String(action.value);
        }
        return String(action.value ?? '');
    };

    return (
        <div className="flex flex-col gap-3">
            <div>
                <label className="text-xs text-content-weak mb-1 block">
                    {t('outputMapper.visualEditor.valueType')}
                </label>
                <SelectField
                    value={valueType}
                    onValueChange={handleTypeChange}
                    options={[
                        { value: 'string', label: 'String' },
                        { value: 'number', label: 'Number' },
                        { value: 'boolean', label: 'Boolean' },
                        { value: 'json', label: 'JSON' },
                    ]}
                />
            </div>

            <div>
                <label className="text-xs text-content-weak mb-1 block">{t('outputMapper.visualEditor.value')}</label>

                {valueType === 'boolean' ? (
                    <div className="flex items-center gap-2">
                        <Switch value={Boolean(action.value)} onValueChange={(checked) => handleValueChange(checked)} />
                        <span className="text-sm text-content-strong">{action.value ? 'true' : 'false'}</span>
                    </div>
                ) : valueType === 'json' ? (
                    <textarea
                        value={getStringValue()}
                        onChange={(e) => handleValueChange(e.target.value)}
                        className="w-full h-24 px-2 py-1.5 text-sm border border-line-weak rounded bg-surface-weak text-content-strong font-mono resize-none"
                        placeholder='{"key": "value"}'
                    />
                ) : (
                    <StringField
                        value={getStringValue()}
                        onValueChange={handleValueChange}
                        placeholder={
                            valueType === 'number'
                                ? t('outputMapper.visualEditor.enterNumber')
                                : t('outputMapper.visualEditor.enterValue')
                        }
                    />
                )}
            </div>
        </div>
    );
};

export default AssignLiteralBlock;
